// Pure helpers for the PC inactivity re-engagement section. Kept framework-free
// (no React, no GrapesJS) so the labels, formatting, and form validation can be
// unit-tested without rendering the heavy email editor.

/** Human-readable copy for each backend eligibility reason. */
export const ELIGIBILITY_LABELS = {
  eligible: "Eligible — would send on the next run",
  alerts_disabled: "Re-engagement email is turned off",
  org_inactive: "Organization is deactivated",
  not_paid: "Only active paid organizations are eligible",
  max_sends_reached: "Maximum re-engagement sends reached",
  test_or_internal_org: "Excluded (test / internal org)",
  within_threshold: "Recently ran the processing checklist",
  cooldown_active: "In cooldown from a recent email",
  no_recipients: "No owner/admin recipients found",
  unknown_baseline: "Not enough history to evaluate",
  claim_in_flight: "A send is already in progress",
  delivery_failed: "Last delivery attempt failed",
};

/** Tone for a reason, used to pick a chip color. */
export function eligibilityTone(reason) {
  if (reason === "eligible") {
    return "ok";
  }

  if (reason === "delivery_failed" || reason === "no_recipients") {
    return "danger";
  }

  return "warn";
}

export function reasonLabel(reason) {
  return ELIGIBILITY_LABELS[reason] || reason || "Unknown";
}

/** Send-record status → display label. */
export const SEND_STATUS_LABELS = {
  sent: "Sent",
  failed: "Failed",
  pending: "Pending",
  skipped: "Skipped",
};

export function sendStatusLabel(status) {
  return SEND_STATUS_LABELS[status] || status || "—";
}

export function sendStatusTone(status) {
  if (status === "sent") {
    return "ok";
  }

  if (status === "failed") {
    return "danger";
  }

  return "warn";
}

/**
 * Describe how long it's been since the last PC run for display.
 *
 * @param {{ daysSinceLastRun: number|null, hasEverRun: boolean } | null} metrics
 * @returns {string}
 */
export function daysSinceLabel(metrics) {
  if (!metrics || metrics.daysSinceLastRun == null) {
    return "—";
  }

  const days = metrics.daysSinceLastRun;
  const suffix = days === 1 ? "day" : "days";

  if (!metrics.hasEverRun) {
    return `${days} ${suffix} (never run)`;
  }

  return `${days} ${suffix}`;
}

/** Clamp a raw input value to a positive integer, or return null. */
export function toPositiveIntOrNull(value) {
  const parsed =
    typeof value === "number" ? value : parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return Math.floor(parsed);
}

/**
 * Validate the editable settings form before saving. Returns a list of
 * human-readable errors (empty when valid).
 *
 * @param {{ thresholdDays: any, cooldownDays: any, maxSends: any, subject: any, emailHtml: any }} form
 * @returns {string[]}
 */
export function validateSettingsForm(form) {
  const errors = [];

  if (toPositiveIntOrNull(form.thresholdDays) == null) {
    errors.push("Inactivity threshold must be a positive number of days.");
  }

  if (toPositiveIntOrNull(form.cooldownDays) == null) {
    errors.push("Cooldown must be a positive number of days.");
  }

  if (toPositiveIntOrNull(form.maxSends) == null) {
    errors.push("Maximum sends must be a positive number.");
  }

  if (typeof form.subject !== "string" || !form.subject.trim()) {
    errors.push("Email subject is required.");
  }

  if (typeof form.emailHtml !== "string" || !form.emailHtml.trim()) {
    errors.push("Email body cannot be empty.");
  }

  return errors;
}

/**
 * Build the sanitized settings patch sent to the backend from the form state.
 *
 * @param {object} form
 * @returns {object}
 */
export function buildSettingsPatch(form) {
  return {
    enabled: form.enabled === true,
    thresholdDays: toPositiveIntOrNull(form.thresholdDays) ?? 30,
    cooldownDays: toPositiveIntOrNull(form.cooldownDays) ?? 30,
    maxSends: toPositiveIntOrNull(form.maxSends) ?? 3,
    subject: form.subject,
    emailHtml: form.emailHtml,
    emailPlainText: form.emailPlainText ?? "",
  };
}

/**
 * Normalize one bulk-send result row for display. `wouldSend` (dry-run) and
 * `sent` (real send) both count as a positive outcome.
 *
 * @param {object} result
 * @returns {{ organizationId: string, sent: boolean, reason: string, recipientCount: number, error: string|null }}
 */
export function normalizeBulkResult(result) {
  const recipients = Array.isArray(result?.recipients) ? result.recipients : [];

  return {
    organizationId: result?.organizationId || "—",
    sent: result?.sent === true || result?.wouldSend === true,
    reason: result?.error ? "delivery_failed" : result?.reason || "unknown",
    recipientCount: recipients.length,
    error: result?.error || null,
  };
}

/**
 * Describe an overview response that does not cover every active org, or null
 * when the list is complete.
 *
 * Both overview endpoints bound their cross-org scan (`truncated`) and fail
 * soft to an empty list on error (`partial`). Neither was surfaced, so a
 * capped or failed scan was indistinguishable from "these are all the orgs" —
 * which silently hid ~230 active orgs from every tab.
 *
 * @param {{ truncated?: boolean, partial?: boolean, totalConsidered?: number } | null} json
 * @returns {string|null}
 */
export function describeIncompleteScan(json) {
  if (json?.partial === true) {
    return "This list is incomplete: the scan failed part-way, so some active organizations are missing. Reload to try again.";
  }

  if (json?.truncated === true) {
    const considered = Number.isFinite(json?.totalConsidered)
      ? `${json.totalConsidered}`
      : "the first batch of";

    return `This list is incomplete: only ${considered} active organizations were scanned, so some are not ranked here.`;
  }

  return null;
}

/** Available client-side sorts for the at-risk overview table. */
export const OVERVIEW_SORTS = {
  MOST_INACTIVE: "most_inactive",
  NAME: "name",
};

/**
 * Sort at-risk overview rows. Never mutates the input.
 *
 * @param {Array<object>} rows
 * @param {string} sort
 * @returns {Array<object>}
 */
export function sortOverviewRows(rows, sort) {
  const copy = Array.isArray(rows) ? rows.slice() : [];

  if (sort === OVERVIEW_SORTS.NAME) {
    return copy.sort((a, b) =>
      String(a.organizationName || "").localeCompare(
        String(b.organizationName || ""),
      ),
    );
  }

  return copy.sort(
    (a, b) => (b.daysSinceLastRun || 0) - (a.daysSinceLastRun || 0),
  );
}

/** Sortable columns for the at-risk overview table. */
export const SORT_COLUMNS = {
  NAME: "name",
  DAYS_SINCE: "daysSinceLastRun",
  LAST_RUN: "lastRunAt",
  ALERTS: "alertEnabled",
};

/**
 * Generic, direction-aware sort for the overview table. Null timestamps
 * (never-run) and null day counts sort as the lowest value so `desc` pushes
 * them to the bottom and `asc` to the top. Never mutates the input.
 *
 * @param {Array<object>} rows
 * @param {string} key - one of SORT_COLUMNS
 * @param {"asc"|"desc"} dir
 * @returns {Array<object>}
 */
export function sortRowsByColumn(rows, key, dir = "desc") {
  const copy = Array.isArray(rows) ? rows.slice() : [];
  const factor = dir === "asc" ? 1 : -1;

  const numeric = (value) => (Number.isFinite(value) ? value : -Infinity);

  const compare = (a, b) => {
    switch (key) {
      case SORT_COLUMNS.NAME:
        return (
          factor *
          String(a.organizationName || "").localeCompare(
            String(b.organizationName || ""),
          )
        );
      case SORT_COLUMNS.LAST_RUN:
        return factor * (numeric(a.lastRunAt) - numeric(b.lastRunAt));
      case SORT_COLUMNS.ALERTS:
        return (
          factor * ((a.alertEnabled ? 1 : 0) - (b.alertEnabled ? 1 : 0))
        );
      case SORT_COLUMNS.DAYS_SINCE:
      default:
        return (
          factor * (numeric(a.daysSinceLastRun) - numeric(b.daysSinceLastRun))
        );
    }
  };

  return copy.sort(compare);
}

/**
 * Replace `{{token}}` placeholders for a client-side email preview. Mirrors the
 * backend renderer so the preview matches what recipients receive. Unknown
 * tokens are left intact.
 *
 * @param {string} template
 * @param {Record<string, string|number>} tokens
 * @returns {string}
 */
export function renderTemplatePreview(template, tokens = {}) {
  if (typeof template !== "string") {
    return "";
  }

  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(tokens, key)) {
      const value = tokens[key];
      return value == null ? "" : String(value);
    }

    return match;
  });
}

/**
 * Sample token map for previews/test emails. Uses a concrete org when one is
 * available so the preview reads realistically.
 *
 * @param {{ organizationName?: string, daysSinceLastRun?: number|null }} [sample]
 */
export function buildPreviewTokens(sample = {}) {
  return {
    firstName: "there",
    organizationName: sample.organizationName || "your team",
    daysSinceLastRun:
      sample.daysSinceLastRun == null ? "many" : sample.daysSinceLastRun,
    thresholdDays: 30,
    appUrl: "https://app.addy.so",
  };
}
